import React, { memo, useReducer, useCallback, useEffect, useMemo, Fragment } from 'react';
import PropTypes from 'prop-types';
import { createUseStyles, Loader, Modal, Text, Advice, Link, Icon } from '@Sage/hoi-poi-ui';

import { downloadURI } from '/lib/helpers';
import { useWeb5Context } from 'lib/Web5Context';
import { getErrors, getWarnings } from 'lib/services/imports';
import { showErrorToast } from 'lib/errors';

import ErrorList from './ErrorList';

import styles from './styles';
const useStyles = createUseStyles(styles, 'ImportedModal');

const initialState = {
    data: {},
    isOpen: false, // FIXME
    loading: false,
    errors: null,
    excelUrlErrors: null,
    excelUrlWarnings: null,
    showError: false,
    errorData: null,
};

function reducer(state, action) {
    switch (action.type) {
        case 'init':
            return {
                ...initialState,
                data: action.data,
                isOpen: true,
                loading: true,
                errors: null,
                excelUrlErrors: null,
                excelUrlWarnings: null,
            };
        case 'close':
            return { ...state, isOpen: false };
        case 'setErrors':
            return {
                ...state,
                errors: action.errors,
                excelUrlErrors: action.excelUrlErrors,
                loading: false,
            };
        case 'setWarnings':
            return {
                ...state,
                warnings: action.warnings,
                excelUrlWarnings: action.excelUrlWarnings,
                loading: false,
            };
        case 'setShowError':
            return { ...state, showError: action.showError, errorData: action.errorData };
        default:
            throw new Error('No action provided');
    }
}

const ImportedModal = ({ onRef, activeRowId, setActiveRowId, ...props }) => {
    const classes = useStyles();
    const { t } = useWeb5Context();
    const [state, dispatch] = useReducer(reducer, initialState);

    // Modal handlers
    const onClose = useCallback(() => {
        setActiveRowId(null);
        dispatch({ type: 'close' });
    }, [dispatch, setActiveRowId]);

    const onDownload = useCallback(
        (isWarning) => {
            if (isWarning) downloadURI(state.excelUrlWarnings);
            else downloadURI(state.excelUrlErrors);
        },
        [state],
    );

    const fetchData = useCallback((id, isErrors, dispatch, onClose, t) => {
        return new Promise((resolve, reject) => {
            let pr;
            if (isErrors) {
                pr = getErrors(id);
            } else {
                pr = getWarnings(id);
            }

            pr.then((result) => {
                const { excelUrl } = result;
                const data = isErrors ? result.errors : result.warnings;

                if (!Array.isArray(data)) {
                    onClose();
                    reject('No data available');
                }

                const actionType = isErrors ? 'setErrors' : 'setWarnings';
                const excelUrlKey = isErrors ? 'excelUrlErrors' : 'excelUrlWarnings';
                const key = isErrors ? 'errors' : 'warnings';

                dispatch({
                    type: actionType,
                    [excelUrlKey]: excelUrl,
                    [key]: data.reduce((arr, item) => {
                        arr = [
                            ...arr,
                            ...item[key].map((e) => ({
                                row: item.row,
                                name: e.name,
                                error: e.error,
                                value: e.value,
                            })),
                        ];
                        return arr;
                    }, []),
                });

                resolve();
            }).catch((error) => {
                console.error(error);
                onClose();
                showErrorToast(t, error);
                reject(error);
            });
        });
    }, []);

    // Child method
    useEffect(() => {
        onRef &&
            onRef({
                open: (data) => {
                    dispatch({ type: 'init', data });

                    if (data.resultNOK) {
                        fetchData(data.id, true, dispatch, onClose, t);
                    }

                    if (data.resultWOK) {
                        fetchData(data.id, false, dispatch, onClose, t);
                    }
                },
            });
    }, [onRef, dispatch, onClose, t, fetchData]);

    // Modal data
    const dataModal = useMemo(() => {
        return state?.loading || state?.showError
            ? {}
            : {
                  onConfirm: onClose,
                  confirmText: t('cfm_label_confirm_ok'),
              };
    }, [state, onClose, t]);

    const setErrorMessage = useCallback(
        (showError, errorData) => dispatch({ type: 'setShowError', showError, errorData }),
        [],
    );

    const listContentModal = useMemo(() => {
        const okAdvice = !!(state.data && state.data.resultOK);
        const warningLiteral =
            state.data.resultWOK > 1
                ? 'cfm_label_import_warning_numbers'
                : 'cfm_label_import_warning_number';
        const errorsLiteral =
            state.data.resultNOK > 1
                ? 'cfm_label_import_error_numbers'
                : 'cfm_label_import_error_number';
        if (!state.data) return null;

        return state.loading ? (
            <Fragment>
                <div className={classes.loadingLoader}>
                    <Loader size="big" />
                </div>
            </Fragment>
        ) : (
            <Fragment>
                {okAdvice && (
                    <Advice className={classes.advice} type="success" showIcon>
                        {t('cfm_label_summary_imported_success', [
                            state.data.resultOK,
                            state.data.resultOK + state.data.resultNOK,
                        ])}
                    </Advice>
                )}
                {!!state.data.resultWOK && (
                    <Advice
                        className={classes.adviceWarning}
                        type="warning"
                        showCollapse={false}
                        showIcon
                    >
                        <div>
                            <Text bold>{t(warningLiteral, [state.data.resultWOK])}</Text>
                        </div>
                        <div className={classes.adviceLiterals}>
                            <Text>{t('cfm_label_import_warning_desc_part1')}</Text>
                            <Link className={classes.linkWarning} onClick={() => onDownload(true)}>
                                {t('cfm_action_download_here')}
                            </Link>{' '}
                            <Text>{t('cfm_label_import_warning_desc_part2')}</Text>
                        </div>
                    </Advice>
                )}
                {!!state.data.resultNOK && (
                    <Advice
                        className={classes.adviceError}
                        type="error"
                        showCollapse={false}
                        showIcon
                    >
                        <div>
                            <Text bold>{t(errorsLiteral, [state.data.resultNOK])}</Text>
                        </div>
                        <div className={classes.adviceLiterals}>
                            <Text>{t('cfm_label_import_error_desc_part1')}</Text>
                            <Link className={classes.linkError} onClick={() => onDownload()}>
                                {t('cfm_action_download_here')}
                            </Link>{' '}
                            <Text>{t('cfm_label_import_warning_desc_part2')}</Text>
                        </div>
                    </Advice>
                )}
                <ErrorList
                    setErrorMessage={setErrorMessage}
                    errors={state.errors}
                    warnings={state.warnings}
                />
            </Fragment>
        );
    }, [
        state.data,
        state.loading,
        state.errors,
        state.warnings,
        classes.loadingLoader,
        classes.advice,
        classes.adviceWarning,
        classes.adviceLiterals,
        classes.linkWarning,
        classes.adviceError,
        classes.linkError,
        t,
        setErrorMessage,
        onDownload,
    ]);

    const showErrorModal = useMemo(() => {
        const advice = state?.errorData?.isWarning
            ? { class: classes?.adviceWarning, type: 'warning' }
            : { class: classes?.adviceError, type: 'error' };
        return (
            <Fragment>
                <Advice className={advice.class} type={advice.type} showIcon>
                    <Text type="bold">{t(state?.errorData?.error)}</Text>
                </Advice>
            </Fragment>
        );
    }, [
        state?.errorData?.isWarning,
        state?.errorData?.error,
        classes?.adviceWarning,
        classes.adviceError,
        t,
    ]);

    const errorTitle = useMemo(() => {
        const title = state?.errorData?.isWarning
            ? 'cfm_label_summary_warning_detail'
            : 'cfm_label_summary_error_detail';
        return (
            <div className={classes?.errorTitle}>
                <Icon
                    className={classes?.arrowIcon}
                    onClick={() => setErrorMessage(false, null)}
                    name="arrowBack"
                    size="large"
                />
                <Text type="h6">{t(title, [state?.errorData?.row, state?.errorData?.name])}</Text>
            </div>
        );
    }, [setErrorMessage, state, t, classes]);

    return (
        <Modal
            title={state.showError ? errorTitle : t('cfm_label_summary')}
            {...dataModal}
            isOpen={state.isOpen}
            onRequestClose={onClose}
        >
            {state.showError ? showErrorModal : listContentModal}
        </Modal>
    );
};

ImportedModal.propTypes = {
    onRef: PropTypes.func,
};

export default memo(ImportedModal);
